import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import {
  ContentLibraryPage,
  DashboardPage,
  DeliveryCenterPage,
  HelpPage,
  NewProjectPage,
  ProjectOverviewPage,
  ProjectsPage,
} from "@/components/pages";
import { ToolsPage } from "@/components/tools";
import { WorkspaceContext } from "@/hooks/useWorkspace";
import { forgetProject, previousKnowledgeActiveId, readRecentProjectIds, rememberProject } from "@/lib/research";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function WorkspaceRouter() {
  const navigate = useNavigate();
  const [projectIds, setProjectIds] = useState(() => {
    const active = previousKnowledgeActiveId();
    return [...new Set([...(active ? [active] : []), ...readRecentProjectIds()])];
  });
  const value = useMemo(() => ({
    projectIds,
    addProject: (id: string) => {
      rememberProject(id);
      setProjectIds((current) => [id, ...current.filter((candidate) => candidate !== id)].slice(0, 24));
    },
    removeProject: (id: string) => {
      forgetProject(id);
      setProjectIds((current) => current.filter((candidate) => candidate !== id));
    },
  }), [projectIds]);
  return <WorkspaceContext.Provider value={value}>
    <Routes>
      <Route element={<AppShell projectIds={projectIds} onStartProject={() => navigate("/projects/new")} />}>
        <Route index element={<DashboardPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<NewProjectPage />} />
        <Route path="projects/:projectId" element={<ProjectOverviewPage />} />
        <Route path="projects/:projectId/content" element={<ContentLibraryPage />} />
        <Route path="projects/:projectId/delivery" element={<DeliveryCenterPage />} />
        <Route path="knowledge" element={<NewProjectPage />} />
        <Route path="library" element={<ContentLibraryPage />} />
        <Route path="deliveries" element={<DeliveryCenterPage />} />
        <Route path="tools" element={<Navigate replace to="/tools/transcripts" />} />
        <Route path="tools/transcripts" element={<ToolsPage />} />
        <Route path="tools/profiles" element={<ToolsPage />} />
        <Route path="profiles" element={<ToolsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="*" element={<Navigate replace to="/" />} />
      </Route>
    </Routes>
  </WorkspaceContext.Provider>;
}

export function App() {
  return <QueryClientProvider client={queryClient}><BrowserRouter><WorkspaceRouter /></BrowserRouter></QueryClientProvider>;
}
